import { useState } from 'react';
import TopBar from '../home/components/TopBar';
import MainNav from '../home/components/MainNav';
import Footer from '../home/components/Footer';
import FacilityGallery from './components/FacilityGallery';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createWebPageSchema } from '../../utils/seo';

export default function KRStoryPage() {
  useSEO({
    title: 'KR Story - KR STUDIO | 회사 소개 및 스튜디오 시설',
    description: 'KR스튜디오의 이야기와 최첨단 스튜디오 시설을 소개합니다. 15년 경력의 전문성과 최고의 제작 환경을 확인하세요.',
    keywords: '회사 소개, KR스튜디오, 스튜디오 시설, 제작 환경, 회사 연혁',
    canonical: '/kr-story',
    schema: createWebPageSchema(
      'KR Story - KR STUDIO',
      'KR스튜디오의 이야기와 최첨단 스튜디오 시설 소개',
      '/kr-story'
    ),
  });

  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [currentFacilityImage, setCurrentFacilityImage] = useState(0);

  const philosophies = [
    {
      title: '시작은 하나의 질문이었습니다',
      subtitle: '"왜 현장의 뜨거운 강의도, 영상에 담기면 그 매력이 반감될까요?"',
      content: '2010년, 강사 매니지먼트를 하던 우리는 고민했습니다. 어떻게 하면 숨겨진 2타 강사를 독보적인 1타 강사로 만들 수 있을까? 해답은 단순한 촬영에 있지 않았습니다. 강사의 숨소리, 표정, 속도감까지 영상 속에 온전히 살아 숨 쉬게 하는 것. 우리는 그들의 콘텐츠가 하나의 브랜드로 탄생하는 순간을 설계하고 싶었습니다. KR스튜디오는 그렇게 시작되었습니다.',
      image: 'https://public.readdy.ai/ai/img_res/b96f5a072a5623af04b5b8423cbe8501.jpg',
      icon: 'ri-question-line',
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: '콘텐츠는 결국 \'사람\'이 완성합니다',
      subtitle: '좋은 장비보다 중요한 것은, 그 앞에 선 사람의 매력을 끌어내는 일입니다',
      content: 'KR은 단순한 촬영 감독이 아닌 \'연출가\'로서, 단순한 편집자가 아닌 \'브랜드 메이커\'로서 강사의 역량을 극대화하는 데 집중합니다. 기술의 중심에는 언제나 \'사람\'이 있어야 한다고 믿기 때문입니다.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20video%20director%20working%20with%20instructor%20in%20modern%20studio%20collaborative%20teamwork%20content%20creation%20warm%20atmosphere%20professional%20filming%20equipment%20human-centered%20approach&width=800&height=600&seq=kr-philosophy2-v2&orientation=landscape',
      icon: 'ri-team-line',
      color: 'from-teal-500 to-teal-600'
    },
    {
      title: '압도적인 속도, 그것은 \'체계\'의 증명입니다',
      subtitle: '24시간 내 납품',
      content: '이는 단순히 서두른다는 뜻이 아닙니다. 사전에 정밀하게 설계된 워크플로우, 숙련된 편집 시스템, 그리고 경험 많은 팀이 만들어낸 확신의 결과입니다. "정말 빠르네요."라는 고객사의 감탄 뒤에는, 1분 1초의 낭비도 허용하지 않는 KR만의 완성된 프로세스가 있습니다.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20video%20editing%20workstation%20multiple%20monitors%20fast%20efficient%20workflow%20modern%20post-production%20studio%20organized%20systematic%20process%20professional%20editor%20working%20focused%20atmosphere&width=800&height=600&seq=kr-philosophy3-v2&orientation=landscape',
      icon: 'ri-time-line',
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: '시작부터 끝까지, 모든 과정을 연결합니다',
      subtitle: '원스톱 솔루션 파트너',
      content: '강사 섭외부터 교안 제작, 촬영, 편집, 그리고 LMS 연동과 운영까지. 교육 영상 제작에 필요한 수많은 단계를 KR스튜디오는 하나로 잇습니다. 복잡한 외주 과정 없이, 고객이 "가장 쉽고 편안하게 시작할 수 있는 이유"입니다. 단순 제작사를 넘어, 우리는 당신의 든든한 원스톱 솔루션 파트너가 되었습니다.',
      image: 'https://readdy.ai/api/search-image?query=Complete%20video%20production%20workflow%20from%20filming%20to%20editing%20modern%20studio%20facility%20comprehensive%20service%20integrated%20system%20professional%20equipment%20organized%20workspace%20end-to-end%20solution&width=800&height=600&seq=kr-philosophy4-v2&orientation=landscape',
      icon: 'ri-links-line',
      color: 'from-orange-500 to-orange-600'
    },
    {
      title: '몰입의 깊이를 더하는 기술',
      subtitle: '최신 기술로 더 나은 학습 경험을',
      content: 'AI 그래픽, AI 휴먼, 자동 편집 워크플로우... 우리는 학습자가 영상 속으로 깊이 빠져들 수 있도록 최신 기술을 적극적으로 도입합니다. 기술은 사람을 돕는 가장 강력한 도구이며, 미디어는 그 변화의 최전선에 있어야 합니다. KR은 오늘도 더 나은 학습 경험을 위해 기술과 콘텐츠의 결합을 고민합니다.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20video%20production%20studio%20with%20AI%20technology%20advanced%20equipment%20innovative%20workflow%20futuristic%20professional%20workspace%20high-tech%20filming%20setup%20clean%20minimalist%20design&width=800&height=600&seq=kr-philosophy5-v2&orientation=landscape',
      icon: 'ri-heart-pulse-line',
      color: 'from-red-500 to-red-600'
    }
  ];

  const facilities = [
    {
      title: '5개 스튜디오 동시 운영',
      icon: 'ri-building-4-line'
    },
    {
      title: '전자칠판·크로마키 전문 촬영',
      icon: 'ri-camera-line'
    },
    {
      title: '편안한 대기공간 완비',
      icon: 'ri-home-smile-line'
    },
    {
      title: '수도권·지방 우수한 접근성',
      icon: 'ri-map-pin-line'
    }
  ];

  const facilityImages = [
    {
      title: '스튜디오 1 - 크로마키',
      image: 'https://readdy.ai/api/search-image?query=Modern%20professional%20video%20production%20studio%20with%20green%20screen%20chroma%20key%20setup%20high-end%20filming%20equipment%20spacious%20clean%20organized%20workspace%20professional%20lighting&width=1200&height=675&seq=kr-facility-1&orientation=landscape'
    },
    {
      title: '스튜디오 2 - 전자칠판',
      image: 'https://readdy.ai/api/search-image?query=Professional%20education%20studio%20with%20electronic%20whiteboard%20interactive%20display%20modern%20teaching%20equipment%20clean%20minimalist%20workspace%20professional%20setup&width=1200&height=675&seq=kr-facility-2&orientation=landscape'
    },
    {
      title: '대기 공간',
      image: 'https://readdy.ai/api/search-image?query=Comfortable%20modern%20waiting%20lounge%20area%20cozy%20seating%20clean%20bright%20space%20professional%20office%20environment%20welcoming%20atmosphere&width=1200&height=675&seq=kr-facility-3&orientation=landscape'
    },
    {
      title: '편집실',
      image: 'https://readdy.ai/api/search-image?query=Professional%20video%20editing%20suite%20multiple%20monitors%20workstation%20modern%20equipment%20organized%20workspace%20high-tech%20editing%20room&width=1200&height=675&seq=kr-facility-4&orientation=landscape'
    },
    {
      title: '스튜디오 5 - 대담형',
      image: 'https://readdy.ai/api/search-image?query=Professional%20interview%20studio%20setup%20comfortable%20seating%20modern%20backdrop%20professional%20lighting%20intimate%20conversation%20space&width=1200&height=675&seq=kr-facility-5&orientation=landscape'
    }
  ];

  // Auto-rotate facility images
  useState(() => {
    const interval = setInterval(() => {
      setCurrentFacilityImage((prev) => (prev + 1) % facilityImages.length);
    }, 4000);
    return () => clearInterval(interval);
  });

  const locations = [
    {
      name: '울산 본사',
      address: '울산광역시 남구 테크노산업로55번길 16',
      phone: '070-8656-8134',
      mapUrl: 'https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=울산광역시+남구+테크노산업로55번길+16&zoom=16'
    },
    {
      name: '서울 지사',
      address: '서울특별시 서초구 서초대로56길 40 3층',
      phone: '070-8656-8134',
      mapUrl: 'https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=서울특별시+서초구+서초대로56길+40&zoom=16'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <MainNav />

      {/* Hero Section */}
      <section className="relative min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Modern%20professional%20video%20production%20studio%20cinematic%20atmosphere%20dramatic%20lighting%20high-end%20filming%20equipment%20clean%20minimalist%20workspace%20inspiring%20creative%20environment%20storytelling%20concept&width=1920&height=900&seq=kr-story-hero-v3&orientation=landscape"
            alt="KR Story"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 text-white text-center">
          <div className="max-w-5xl mx-auto">
            <div className="inline-block px-6 py-2 bg-secondary/20 backdrop-blur-sm border border-secondary/30 rounded-full mb-6">
              <span className="text-secondary font-bold text-sm">KR STUDIO STORY</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
              KR Story
            </h1>
            
            <p className="text-xl md:text-2xl mb-6 font-light text-gray-200 leading-relaxed">
              우리는 '기록'이 아닌, '가치'를 만듭니다
            </p>

            <div className="flex items-center justify-center gap-3 text-gray-300 mt-12">
              <i className="ri-scroll-line text-2xl animate-bounce"></i>
              <span className="text-sm">스크롤하여 우리의 이야기를 읽어보세요</span>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Sections */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto space-y-24">
            {philosophies.map((philosophy, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-10 items-center`}>
                {/* Image */}
                <div className="flex-1 relative group">
                  <div className="aspect-[4/3] overflow-hidden rounded-2xl shadow-xl">
                    <img 
                      src={philosophy.image}
                      alt={philosophy.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className={`absolute -top-4 -left-4 w-20 h-20 bg-gradient-to-br ${philosophy.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <i className={`${philosophy.icon} text-4xl text-white`}></i>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-primary mb-3 leading-tight">{philosophy.title}</h2>
                  <h3 className="text-lg font-bold text-secondary mb-4 leading-relaxed">{philosophy.subtitle}</h3>
                  <p className="text-base text-gray-700 leading-relaxed">
                    {philosophy.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outro Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-secondary to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-rocket-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-black text-primary mb-5 leading-tight">
              우리가 만드는 건,<br />단순한 교육 영상이 아닙니다
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              하나의 강의가 누군가를 성장시키고, 하나의 콘텐츠가 기업의 문화를 바꾸는 순간을 우리는 매일 목격합니다.<br />
              기록은 곧 브랜드입니다.<br />
              KR스튜디오는 오늘도 교육을 통해 세상을 바꾸는 가치 있는 일들을 기록하고 있습니다.
            </p>
          </div>
        </div>
      </section>

      {/* Founders Message */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-black mb-3 text-primary">대표 메시지</h2>
              <p className="text-lg text-gray-600">김민규, 류인국 공동대표</p>
            </div>

            <div className="mb-12">
              <div className="relative group">
                <div className="aspect-[16/9] overflow-hidden rounded-2xl shadow-xl">
                  <img 
                    src="https://static.readdy.ai/image/01721397da55af21878e201cee4469af/9befef2dea2e075164cdb5655fa02e5e.png"
                    alt="김민규, 류인국 공동대표"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary via-gray-900 to-primary p-10 rounded-2xl shadow-xl text-white">
              <div className="flex items-start gap-4 mb-6">
                <i className="ri-double-quotes-l text-5xl text-secondary"></i>
              </div>
              <p className="text-lg leading-relaxed mb-6 font-light">
                2010년, 처음 이 일을 시작했을 때 품었던 질문이 있습니다. "한 번의 강의가 누군가의 인생을 바꿀 수 있다면, 우리는 무엇을 해야 할까?"<br /><br />
                그 질문이 지금의 KR스튜디오를 만들었습니다. 우리가 만드는 영상은 단순한 디지털 파일이 아닙니다. 누군가에게는 성장의 기회이고, 기업에게는 혁신의 불씨가 되는 소중한 자산입니다.<br /><br />
                그렇기에 우리는 대충 만들 수 없습니다. 최고의 몰입을 위한 기술, 강사의 역량을 끌어올리는 연출, 빈틈없는 프로세스까지. 이 모든 것은 교육을 통해 더 나은 세상을 만들고자 하는 여러분의 열정을 가장 완벽하게 기록하기 위함입니다.<br /><br />
                기록은 곧 역사이자 브랜드입니다. 가치 있는 그 길에 KR스튜디오가 함께하겠습니다.
              </p>
              
              <div className="flex items-center gap-4">
                <div className="h-1 w-16 bg-secondary rounded-full"></div>
                <span className="text-gray-300 text-sm">김민규, 류인국 공동대표</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black mb-3 text-primary">시설 소개</h2>
            <p className="text-lg text-gray-600">최고의 환경에서 최고의 콘텐츠를</p>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Facility Image Gallery */}
            <FacilityGallery />

            {/* Facility Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-secondary/10 rounded-full mx-auto mb-3">
                  <i className="ri-building-line text-2xl text-secondary"></i>
                </div>
                <h3 className="font-bold text-base text-primary leading-snug">5개 스튜디오<br />동시 운영</h3>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-secondary/10 rounded-full mx-auto mb-3">
                  <i className="ri-camera-line text-2xl text-secondary"></i>
                </div>
                <h3 className="font-bold text-base text-primary leading-snug">전자칠판·크로마키<br />전문 촬영</h3>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-secondary/10 rounded-full mx-auto mb-3">
                  <i className="ri-home-smile-line text-2xl text-secondary"></i>
                </div>
                <h3 className="font-bold text-base text-primary leading-snug">편안한<br />대기공간 완비</h3>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-secondary/10 rounded-full mx-auto mb-3">
                  <i className="ri-map-pin-line text-2xl text-secondary"></i>
                </div>
                <h3 className="font-bold text-base text-primary leading-snug">수도권·지방<br />우수한 접근성</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black mb-3 text-primary">함께한 파트너들</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              300개 이상의 대학교, 기업, 공공기관이<br />
              KR스튜디오와 함께 교육 콘텐츠의 새로운 기준을 만들어가고 있습니다
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-building-line', label: '대학교 및\n교육기관', count: '120+' },
              { icon: 'ri-briefcase-line', label: '기업\n교육 부서', count: '100+' },
              { icon: 'ri-government-line', label: '공공기관\n및 협회', count: '50+' },
              { icon: 'ri-global-line', label: '이러닝\n전문 기업', count: '30+' }
            ].map((category, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-secondary to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${category.icon} text-3xl text-white`}></i>
                </div>
                <div className="text-2xl font-black text-primary mb-2">{category.count}</div>
                <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{category.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subsidiary Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-white p-10 rounded-2xl shadow-xl">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-gradient-to-br from-secondary to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-building-4-line text-4xl text-white"></i>
              </div>
              <div className="flex-1">
                <div className="inline-block px-4 py-1 bg-secondary/10 rounded-full mb-3">
                  <span className="text-secondary font-bold text-sm">자회사</span>
                </div>
                <h2 className="text-2xl font-black text-primary mb-3">온케어</h2>
                <p className="text-lg text-gray-700 mb-5 leading-relaxed">
                  LMS와 온라인 교육 운영의 모든 것<br />
                  KR스튜디오가 만든 또 하나의 혁신
                </p>
                <a 
                  href="https://eoncare.co.kr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-secondary to-blue-600 text-white font-bold rounded-xl hover:shadow-lg transition cursor-pointer whitespace-nowrap"
                >
                  <span>온케어 바로가기</span>
                  <i className="ri-arrow-right-line text-lg"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black mb-3 text-primary">오시는 길</h2>
            <p className="text-lg text-gray-600">KR스튜디오를 찾아오세요</p>
          </div>

          <div className="max-w-6xl mx-auto space-y-12">
            {locations.map((location, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-black text-primary mb-6 text-center">{location.name}</h3>
                
                <div className="aspect-video overflow-hidden rounded-xl shadow-lg mb-6">
                  <iframe
                    src={location.mapUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white p-5 rounded-xl shadow-md flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-secondary to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-white"></i>
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-bold text-primary mb-1 text-sm">주소</h4>
                      <p className="text-gray-600 text-sm break-words">{location.address}</p>
                    </div>
                  </div>
                  <div className="bg-white p-5 rounded-xl shadow-md flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-secondary to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-white"></i>
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-bold text-primary mb-1 text-sm">전화</h4>
                      <p className="text-gray-600 text-sm">{location.phone}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-teal-500 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-5">
            당신의 콘텐츠를 함께 만들어갑니다
          </h2>
          <p className="text-lg md:text-xl mb-8 text-gray-100">
            가치 있는 기록, 지금 시작하세요
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center hidden">
            <a 
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="px-10 py-4 bg-white text-primary font-bold text-lg hover:bg-gray-100 transition cursor-pointer inline-block whitespace-nowrap rounded-xl"
            >
              서비스 둘러보기
            </a>
            <a 
              href="mailto:contact@krstudio.com"
              className="px-10 py-4 border-2 border-white text-white font-bold text-lg hover:bg-white hover:text-primary transition cursor-pointer inline-block whitespace-nowrap rounded-xl"
            >
              문의하기
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <QuickMenu />
    </div>
  );
}
